<template>
  <div class="fee-reminder-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Fee Reminder Notices</h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ notices.length }} Notices
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-box">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Generating reminder notices...</p>
      <p class="text-muted small">Fetching outstanding data from server</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5 error-box">
      <div class="error-icon-wrapper mb-3">
        <feather-icon icon="AlertCircleIcon" size="64" class="text-danger" />
      </div>
      <h3 class="text-danger font-weight-bold">Report Error</h3>
      <p class="text-muted mb-4">{{ error }}</p>
      <b-button variant="primary" pill @click="fetchData" class="px-3">
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
        Retry Loading
      </b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="notices-container">
      <div
        v-for="(student, index) in notices"
        :key="student.studentID"
        class="notice-page"
      >
        <div class="reminder-notice">
          <!-- Premium Header Design -->
          <!-- Professional Header (Matching StudentEmailPrint style) -->
          <div class="report-header-modern mb-4">
            <div class="d-flex align-items-center">
              <div class="logo-wrapper mr-3" v-if="schoolLogo">
                <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
              </div>
              <div class="header-text-block">
                <h2 class="school-name-full mb-0">
                  {{ campusName
                  }}<span v-if="campusBranch" class="text-muted">
                    - {{ campusBranch }}</span
                  >
                </h2>
                <div class="report-info-line mt-1">
                  <span class="report-name-text">Fee Reminder Notice</span>
                  <span class="date-range-text">
                    <strong>Generated on {{ currentDateTime }}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Simple Body Pattern -->
          <div class="date-right text-right mb-4 mt-2">
            <strong>Date:</strong>
            <span class="underline-value px-4">{{ currentDate }}</span>
          </div>

          <div class="student-info-grid mb-5">
            <div class="row mb-4">
              <div class="col-6 d-flex align-items-end">
                <strong style="min-width: 85px">Student ID:</strong>
                <div
                  class="underline-value flex-grow-1 text-center font-weight-bold"
                >
                  {{ student.grNo || student.studentID }}
                </div>
              </div>
              <div class="col-6 d-flex align-items-end">
                <strong style="min-width: 115px">Student Name:</strong>
                <div class="underline-value flex-grow-1 text-center">
                  {{ student.studentName }}
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-6 d-flex align-items-end">
                <strong style="min-width: 80px">Class/Div:</strong>
                <div class="underline-value flex-grow-1 text-center">
                  {{ student.className }}
                  <span v-if="student.sectionName"
                    >- {{ student.sectionName }}</span
                  >
                </div>
              </div>
              <div class="col-6 d-flex align-items-end">
                <strong style="min-width: 140px">Outstanding Fees:</strong>
                <div
                  class="underline-value flex-grow-1 text-center font-weight-bold"
                >
                  {{ formatCurrency(student.totalOutstanding) }}
                </div>
              </div>
            </div>
          </div>

          <div class="simple-message-body mt-5">
            <p class="mb-4">Dear Parents,</p>

            <p class="mb-4 lh-lg text-justify">
              We hereby request you to clear the outstanding dues for the school
              fees. Your prompt action to this will be highly appreciated.
              Kindly pay the entire pending fee by
              <span class="underline-value font-weight-bold px-4 text-center">{{
                formatDate(student.expiryDate)
              }}</span>
              to avoid inconvenience.
            </p>

            <p class="mb-5">
              Thank you for your continued support throughout the year.
            </p>
          </div>

          <div class="issued-by-section mt-5 pt-5 d-flex align-items-end">
            <strong class="mr-3">Issued By:</strong>
            <span>School Management</span>
          </div>
        </div>

        <!-- Page Break for Multi-notice Print -->
        <div
          class="page-break"
          v-if="(index + 1) % 2 === 0 && index < notices.length - 1"
        ></div>
      </div>

      <!-- No Data State -->
      <div v-if="notices.length === 0" class="text-center py-5 no-data-box">
        <feather-icon icon="InboxIcon" size="64" class="text-muted mb-3" />
        <h4 class="text-muted">No Outstanding Records Found</h4>
        <p class="small">
          There are no students matching your filter criteria with outstanding
          fees.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge, BButton } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "FeeReminderPrint",
  components: {
    BSpinner,
    BBadge,
    BButton,
  },
  data() {
    return {
      loading: false,
      error: null,
      notices: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      classID: "",
      secID: "",
      search: "",
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.classID = query.classID;
      this.secID = query.secID;
      this.search = query.search || "";

      if (!this.db || !this.cID) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.fetchData();
    },
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;

        // Load Campus Info first
        await this.loadCampusInfo();

        // Fetch Fee Reminder Data
        const baseUrl = this.$store.state.domain;
        const url = `${baseUrl}FeeReminder/GetFeeReminderData`;

        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            classID: this.classID || 0,
            secID: this.secID || 0,
            search: this.search,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.notices = response.data.data || [];
        } else {
          this.error =
            response.data?.message || "Failed to load reminder data.";
        }
      } catch (error) {
        console.error("Failed to load fee reminder:", error);
        this.error =
          "Network error or server unavailable. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
            this.campusAddress = match.address || "";
          } else if (response.data.data.length > 0) {
            const info = response.data.data[0];
            this.campusName = info.name;
            this.campusBranch = info.branch;
            this.schoolLogo = info.logo;
            this.campusAddress = info.address || "";
          }
        } else if (
          response.data &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          const match = response.data.find((c) => c.id == this.cID);
          const info = match || response.data[0];
          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address || "";
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || null;
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "Rs. 0.00";
      return (
        "Rs. " +
        new Intl.NumberFormat("en-PK", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)
      );
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.fee-reminder-report {
  background: #f4f7fa;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2c3e50;
}

/* Control Bar Styles */
.control-bar {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.8);

  h4 {
    font-weight: 800;
    color: #1e293b;
    font-size: 1.2rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);

  &:active {
    transform: scale(0.98);
  }
}

.btn-print {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: white;
  &:hover {
    box-shadow: 0 6px 15px rgba(115, 103, 240, 0.3);
  }
}

.btn-excel {
  background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
  color: white;
  &:hover {
    box-shadow: 0 6px 15px rgba(40, 199, 111, 0.3);
  }
}

.btn-close {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  &:hover {
    background: #f8fafc;
    color: #ef4444;
  }
}

/* Notice Container */
.notices-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
}

.notice-page {
  background: white;
  width: 95%;
  max-width: 1100px;
  min-height: 148mm; /* Half A4 or similar */
  padding: 3rem 4rem;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

/* Reminder Notice Styles */
.reminder-notice {
  display: flex;
  flex-direction: column;
}

/* Professional Header Modern */
.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;
  position: relative;

  .school-logo-img {
    height: 110px;
    max-width: 320px;
    object-fit: contain;
  }

  .header-text-block {
    flex: 1;
    min-width: 0;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    word-break: break-word;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

/* Simple Body Pattern Styles */
.date-right {
  font-size: 1.05rem;
  color: #000;
}

.student-info-grid {
  font-size: 1.05rem;

  strong {
    color: #000;
  }
}

.underline-value {
  border-bottom: 1px solid #000;
  display: inline-block;
  min-height: 24px;
  color: #000;
}

.simple-message-body {
  font-size: 1.05rem;
  color: #000;

  .lh-lg {
    line-height: 2 !important;
  }

  .text-justify {
    text-align: justify;
  }
}

.issued-by-section {
  font-size: 1.05rem;
  color: #000;
}

.system-footer {
  margin-top: 40px;
  padding: 15px 10px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.page-break {
  page-break-after: always;
  height: 0;
}

/* Print Overrides */
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0;
    padding: 0;
  }

  .fee-reminder-report {
    background: white !important;
    padding: 0 !important;
  }

  .notices-container {
    gap: 0 !important;
    display: block !important;
  }

  .notice-page {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 12mm 15mm !important;
    width: 100% !important;
    height: 148.5mm !important; /* Exactly half A4 page */
    min-height: 148.5mm !important;
    max-height: 148.5mm !important;
    box-sizing: border-box;
    page-break-inside: avoid;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .reminder-notice {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
    height: 100% !important;
  }

  .issued-by-section {
    margin-top: auto !important;
    padding-bottom: 2mm !important;
  }

  /* Relax Print Styles for a balanced letter look */
  .reminder-notice .mb-5 {
    margin-bottom: 1.5rem !important;
  }
  .reminder-notice .mt-5 {
    margin-top: 1.5rem !important;
  }
  .reminder-notice .mb-4 {
    margin-bottom: 1rem !important;
  }
  .reminder-notice .mt-4 {
    margin-top: 1rem !important;
  }
  .reminder-notice .pt-5 {
    padding-top: 1rem !important;
  }

  .report-header-modern .school-logo-img {
    height: 70px !important;
  }

  .school-name-full {
    font-size: 1.4rem !important;
  }

  .simple-message-body .lh-lg {
    line-height: 1.5 !important;
  }

  /* Add dashed cut line between the two notices on a single page */
  .notice-page:nth-child(odd) {
    border-bottom: 1px dashed #ccc !important;
  }

  .report-header-modern {
    border-bottom: 2px solid #000 !important;
  }
}
</style>
